/**
 * Same-tab WhatsApp signup return (/whatsapp/connect/callback). Meta redirects here; the portal worker moves the
 * validated state + code into the fragment (never in a Referer or access log). This screen strips the fragment,
 * POSTs state + code to /v1/whatsapp-connect/embedded-signup/redirect-complete (single-use, tenant-bound state;
 * the API exchanges the code with the same redirect URI and finds the WABA + number from Meta), and when Meta
 * shared several numbers lets the user pick one (/embedded-signup/pick).
 * The page loads nothing from Meta, so it keeps the strict portal CSP.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { SignupNumberOption, SignupRedirectResult } from '@qm/contracts/whatsapp-connect';
import { Button, ErrorState, Heading, Loading, Panel, Text } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { bffJson } from '../../../lib/bff';
import { parseSignupCallback } from './signup-redirect';
import { signupErrorKey, type WaNumber } from './WhatsAppConnectPage';

type Phase = 'working' | 'choose' | 'done' | 'error';
const CONNECT_PATH = '/whatsapp/connect';

export function WhatsAppConnectCallbackPage() {
  const { t } = useApp();
  const [phase, setPhase] = useState<Phase>('working');
  const [errorKey, setErrorKey] = useState('whatsapp-connect.signup.failed');
  const [connected, setConnected] = useState<string | null>(null);
  const [pick, setPick] = useState<{ pickId: string; options: SignupNumberOption[] } | null>(null);
  const [chosen, setChosen] = useState<string>('');
  const started = useRef(false);

  const fail = (key: string) => { setErrorKey(key); setPhase('error'); };
  const done = (n: Pick<WaNumber, 'displayPhoneNumber' | 'verifiedName'> & { phoneNumberId?: string }) => {
    setConnected(n.displayPhoneNumber ?? n.verifiedName ?? n.phoneNumberId ?? '');
    setPhase('done');
  };

  useEffect(() => {
    if (typeof window === 'undefined' || started.current) return;
    started.current = true;
    const p = parseSignupCallback(window.location.hash);
    // the code is single use: never left in history or re-posted on reload
    history.replaceState(null, '', window.location.pathname);
    if (p.kind === 'cancelled' || p.kind === 'provider_error') {
      fail(p.kind === 'cancelled' ? 'whatsapp-connect.signup.cancelled' : p.errorKey);
      // audit Meta's codes against this attempt; the screen does not depend on the answer
      bffJson('/v1/whatsapp-connect/embedded-signup/provider-error', 'POST', p.report).catch(() => undefined);
      return;
    }
    if (p.kind !== 'code') { fail('whatsapp-connect.signup.failed'); return; }
    bffJson<SignupRedirectResult>('/v1/whatsapp-connect/embedded-signup/redirect-complete', 'POST', { state: p.state, code: p.code }).then((r) => {
      if (r.outcome === 'connected') { done(r.number); return; }
      setPick({ pickId: r.pickId, options: r.options });
      setChosen(r.options.length === 1 ? `${r.options[0]!.wabaId}:${r.options[0]!.phoneNumberId}` : '');
      setPhase('choose');
    }, (e: unknown) => fail(signupErrorKey(e)));
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const option = pick?.options.find((o) => `${o.wabaId}:${o.phoneNumberId}` === chosen);
    if (!pick || !option) return;
    setPhase('working');
    try {
      done(await bffJson<WaNumber & { phoneNumberId: string }>('/v1/whatsapp-connect/embedded-signup/pick', 'POST', { pickId: pick.pickId, wabaId: option.wabaId, phoneNumberId: option.phoneNumberId }));
    } catch (err) { fail(signupErrorKey(err)); }
  };

  return (
    <section className="qm-page" aria-labelledby="page-title">
      <Heading level={1} id="page-title">{t('whatsapp-connect.callback.title')}</Heading>
      <Panel aria-labelledby="wa-callback">
        <Heading level={2} id="wa-callback">{t('whatsapp-connect.page.title')}</Heading>
        {phase === 'working' ? <Loading label={t('whatsapp-connect.callback.working')} /> : null}
        {phase === 'error' ? <div role="alert"><ErrorState message={t(errorKey)} /></div> : null}
        {phase === 'done' ? <Text role="status">{t('whatsapp-connect.signup.success', { number: connected ?? '' })}</Text> : null}
        {phase === 'choose' && pick ? (
          <form onSubmit={(e) => void submit(e)}>
            <fieldset>
              <legend>{t('whatsapp-connect.callback.choose')}</legend>
              {pick.options.map((o, i) => {
                const value = `${o.wabaId}:${o.phoneNumberId}`;
                return (
                  <div key={value}>
                    <input type="radio" name="wa-number" id={`wa-number-${i}`} value={value} checked={chosen === value} onChange={() => setChosen(value)} />
                    <label htmlFor={`wa-number-${i}`}><bdi dir="ltr">{o.displayPhoneNumber ?? o.phoneNumberId}</bdi>{o.verifiedName ? <> · <bdi>{o.verifiedName}</bdi></> : null}</label>
                  </div>
                );
              })}
            </fieldset>
            <Button type="submit" variant="primary" disabled={!chosen}>{t('whatsapp-connect.callback.connect')}</Button>
          </form>
        ) : null}
        {phase === 'done' || phase === 'error' ? <a className="qm-button" href={CONNECT_PATH}>{t('whatsapp-connect.callback.back')}</a> : null}
      </Panel>
    </section>
  );
}
